import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { FeaturesProvider, useFeatures } from '@/contexts/FeaturesContext';
import '@/i18n'; // Initialize i18n
import { EmailVerification } from '@/components/auth/EmailVerification';
import { Button } from '@/components/ui/button';

// End-user pages
import { HomePage } from '@/pages/HomePage';
import { ActivityPage } from '@/pages/ActivityPage';
import { BookingPage } from '@/pages/BookingPage';
import { AboutPage } from '@/pages/AboutPage';
import { WhatWeOfferPage } from '@/pages/WhatWeOfferPage';
import { ContactPage } from '@/pages/ContactPage';
import { CustomAdventuresPage } from './pages/public/CustomAdventures';
import { TestMapPage } from '@/pages/TestMapPage';
import { ForBusinessPage } from '@/pages/ForBusinessPage';
import { BusinessProfilePage } from '@/pages/public/BusinessProfilePage';

// Business user pages
import { BusinessLayout } from '@/components/business/layouts/BusinessLayout';
import { AnalyticsDashboard } from '@/components/business/features/analytics/AnalyticsDashboard';
import { ActivityManagement } from '@/components/business/features/activities/ActivityManagement';
import { BookingManagement } from '@/components/business/features/bookings/BookingManagement';
import { CustomAdventuresDashboard } from '@/components/business/features/custom-adventures/CustomAdventuresDashboard';
import { BusinessHomePage } from '@/components/business/homepage/BusinessHomePage';
import { CommunicationDashboard } from '@/components/business/features/communication/CommunicationDashboard';
import { StaffDashboard } from '@/components/business/features/staff/StaffManagement';
import { SettingsDashboard } from '@/components/business/features/settings/SettingsDashboard';
import { ActivityCalendar } from '@/components/business/features/calendar/ActivityCalendar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FundingDashboard } from '@/components/business/features/funding/FundingDashboard';
import { MappingDashboard } from '@/components/business/features/mapping/MappingDashboard';
import { PartnerAnalyticsDashboard } from '@/components/business/features/partner/PartnerAnalyticsDashboard';
import { InternalToolPage } from '@/pages/InternalToolPage';
import { LivestockManagementPage } from '@/components/business/features/livestock/LivestockManagementPage';
import PaymentPage from '@/pages/PaymentPage';
import Confirmation from '@/pages/Confirmation';
import CustomAdventureRequestConfirmation from '@/pages/CustomAdventureRequestConfirmation';
import CustomAdventureConfirmation from '@/pages/CustomAdventureConfirmation';
import CustomAdventurePayment from '@/pages/CustomAdventurePayment';
import CustomAdventureFinalConfirmation from '@/pages/CustomAdventureFinalConfirmation';
import BookingStatus from '@/pages/BookingStatus';
import { useEffect } from 'react';
import { ScrollToTop } from '@/components/ScrollToTop';

// Protected Route Component
function ProtectedBusinessRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // Check business auth first, then fallback to public auth
    const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
    const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
    const auth = businessAuth.token ? businessAuth : publicAuth;
    
    if (!auth.token) {
      navigate('/');
    }
  }, [navigate]);

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#77A146]" />
    </div>;
  }

  if (!user || (user.role !== 'business' && user.role !== 'admin')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Feature-gated route: renders children only when the flag is true, otherwise redirects home
function FlaggedRoute({ flag, children }: { flag: boolean; children: React.ReactNode }) {
  return flag ? <>{children}</> : <Navigate to="/" replace />;
}

// App Routes Component with AuthProvider wrapper
function AppRoutesWithAuth() {
  const features = useFeatures();
  return (
    <AuthProvider>
      <div className="min-h-screen bg-[#F0F4F0]">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/what-we-offer" element={<WhatWeOfferPage />} />
          <Route path="/contact" element={<ContactPage />} />
          {/* Booking flow — only when bookingPlatform flag is on */}
          <Route path="/activity/:id"           element={<FlaggedRoute flag={features.bookingPlatform}><ActivityPage /></FlaggedRoute>} />
          <Route path="/booking/:id"            element={<FlaggedRoute flag={features.bookingPlatform}><BookingPage /></FlaggedRoute>} />
          <Route path="/payment"                element={<FlaggedRoute flag={features.bookingPlatform}><PaymentPage /></FlaggedRoute>} />
          <Route path="/confirmation/:bookingId" element={<FlaggedRoute flag={features.bookingPlatform}><Confirmation /></FlaggedRoute>} />
          <Route path="/booking-status/:requestNumber" element={<FlaggedRoute flag={features.bookingPlatform}><BookingStatus /></FlaggedRoute>} />

          {/* Custom adventures — only when customAdventures flag is on */}
          <Route path="/custom-adventure-request-confirmation/:requestNumber" element={<FlaggedRoute flag={features.customAdventures}><CustomAdventureRequestConfirmation /></FlaggedRoute>} />
          <Route path="/custom-adventure-confirmation/:requestNumber"         element={<FlaggedRoute flag={features.customAdventures}><CustomAdventureConfirmation /></FlaggedRoute>} />
          <Route path="/custom-adventure-payment"                             element={<FlaggedRoute flag={features.customAdventures}><CustomAdventurePayment /></FlaggedRoute>} />
          <Route path="/custom-adventure-final-confirmation/:requestNumber"   element={<FlaggedRoute flag={features.customAdventures}><CustomAdventureFinalConfirmation /></FlaggedRoute>} />
          <Route path="/custom-adventures/:businessId"                        element={<FlaggedRoute flag={features.customAdventures}><CustomAdventuresPage /></FlaggedRoute>} />
          <Route path="/test-map" element={<TestMapPage />} />
          <Route path="/for-business" element={<ForBusinessPage />} />
          <Route path="/business/:businessId" element={<BusinessProfilePage />} />
          
          {/* Auth Routes */}
          <Route path="/verify-email/:token" element={<EmailVerification />} />
          
          {/* Business Routes */}
          <Route
            path="/business"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <BusinessHomePage />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/analytics"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <AnalyticsDashboard />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/activities"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <ActivityManagement />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/bookings"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <BookingManagement />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/custom-adventures"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <CustomAdventuresDashboard />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/calendar"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <ActivityCalendar />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/communication"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <CommunicationDashboard />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/staff"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <StaffDashboard />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
      path="/business/funding"
      element={
        <ProtectedBusinessRoute>
          <BusinessLayout>
            <FundingDashboard />  
          </BusinessLayout>
        </ProtectedBusinessRoute>
      }
      errorElement={
        <ErrorBoundary 
          fallback={
            <div className="flex flex-col items-center justify-center p-4 text-center">
              <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
              >
                Reload Page
              </Button>
            </div>
          }
        />
      }
    />

    <Route
      path="/business/partner-stats"
      element={
        <ProtectedBusinessRoute>
          <BusinessLayout>
            <PartnerAnalyticsDashboard />
          </BusinessLayout>
        </ProtectedBusinessRoute>
      }
    />

    <Route
      path="/business/mapping"
      element={
        <ProtectedBusinessRoute>
          <BusinessLayout>
            <MappingDashboard />
          </BusinessLayout>
        </ProtectedBusinessRoute>
      }
      errorElement={
        <ErrorBoundary 
          fallback={
            <div className="flex flex-col items-center justify-center p-4 text-center">
              <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
              >
                Reload Page
              </Button>
            </div>
          }
        />
      }
    />

    <Route
            path="/business/livestock"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <LivestockManagementPage />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          <Route
            path="/business/settings"
            element={
              <ProtectedBusinessRoute>
                <BusinessLayout>
                  <SettingsDashboard />
                </BusinessLayout>
              </ProtectedBusinessRoute>
            }
            errorElement={
              <ErrorBoundary 
                fallback={
                  <div className="flex flex-col items-center justify-center p-4 text-center">
                    <h2 className="text-xl font-bold text-red-600 mb-2">Route Error</h2>
                    <Button
                      onClick={() => window.location.reload()}
                      variant="outline"
                    >
                      Reload Page
                    </Button>
                  </div>
                }
              />
            }
          />
          {/* Internal founder tool — standalone, own auth */}
          <Route path="/internal-tool" element={<InternalToolPage />} />

          {/* 404 Route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

// Main App Component
export default function App() {
  return (
    <FeaturesProvider>
      <ScrollToTop />
      <AppRoutesWithAuth />
    </FeaturesProvider>
  );
}
